import type { ReactNode } from 'react';

import styles from '@/components/book/Crumb.module.css';
import { useBookNavigation } from '@/lib/bookNavigation';
import { Link } from '@/lib/navigation';

export interface CrumbItem {
  label: string;
  /** Where it goes; without one the item is a word, not a link (a place's own kicker, *Places*). */
  href?: string;
  /** A document title. */
  italic?: boolean;
  /** Takes the reader there in place (a page that keeps its own history) instead of following `href`; a modified click still opens `href`. */
  onSelect?: () => void;
  /** The section the reader goes up from, so the page above lands on its row. */
  land?: { section: string };
}

interface CrumbProps {
  items: CrumbItem[];
  /** Right-aligned, e.g. the accession number `No. 12`. */
  aside?: ReactNode;
}

/**
 * `Shelf / *Document*` above a title. Crumbs replace the old in-content back
 * links, and go up (WQ6): Back when the page above is the entry just behind,
 * else a push.
 */
export function Crumb({ items, aside }: CrumbProps) {
  const book = useBookNavigation();
  return (
    <nav className={styles.crumb} aria-label="Crumb">
      {items.map((item) => (
        <span key={`${item.href} ${item.label}`} className={styles.item}>
          {item.href === undefined ? <span className={styles.word}>{item.label}</span> : <Link
            to={item.href}
            className={[styles.link, item.italic ? styles.italic : ''].filter(Boolean).join(' ')}
            onClick={(e) => {
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              if (item.onSelect) item.onSelect();
              else book.up(item.href!, item.land ? { land: item.land } : undefined);
            }}
          >
            {item.label}
          </Link>}
        </span>
      ))}
      {aside != null && <span className={styles.aside}>{aside}</span>}
    </nav>
  );
}
